import React, { useMemo, useState } from 'react';
import { Select, Tag, Tooltip } from 'antd';
import {
  MenuItemText,
} from '../../../../../../../shared/globalStyling/styledText';
import {
  formatNumber,
  formatNumber2SigFig,
} from '../../../../../../../../core/utils/campaigns';
import LineItemsDonut from './LineItemsDonut';
import LineItemsModal from './LineItemsModal';
import { randomColors } from '../../../../../../../../core/utils/colors';
import { TagWithConfig } from '../../../../../shared/StatusTag';
import { tagStatusConfig } from '../../../../utils';
import DataTable from '../../../../../../../shared/designSystem/components/DataTable';

const { Option } = Select;

const SHOW_BY_OPTIONS = [
  { value: 'impressions', text: 'Ad Impressions' },
  { value: 'clicks', text: 'Clicks' },
];

// =============================================================================
// LineItemsCard — campaign line items breakdown.
//
// Structural parity with Creatives / Publishers / Segments (unified card on
// top of DataTable + BreakdownRingChart). Shape-specific differences:
//   - NO thumbnail / accent dot (line items are name + meta rows)
//   - Status and Value Add columns use AntD Tag treatments (preserved)
//   - Action column is a LineItemsModal trigger — kept as-is since the modal
//     renders its own inline button + opens a rich detail panel
//   - Breakdown donut is 'impressions' or 'clicks' only (no CTR dimension
//     in v1 for line items)
// =============================================================================

const numericSort = (key) => (rowA, rowB) => {
  return (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);
};

const nameSort = (rowA, rowB) => {
  const a = rowA.original.lineItemName || '';
  const b = rowB.original.lineItemName || '';
  return String(a).localeCompare(String(b));
};

const LineItemsCard = ({ data }) => {
  const [lineItemDimension, setLineItemDimension] = useState('impressions');

  // Stable colors per row — random-once via useMemo keyed on data identity,
  // so slice colors don't reshuffle on every parent re-render (v1 bug where
  // colors flickered was due to randomColors() being called inline).
  const coloredData = useMemo(
    () =>
      (data || []).map((d) => ({
        ...d,
        value: d.lineItemName,
        color: randomColors(),
      })),
    [data]
  );

  const columns = [
    {
      id: 'lineItemName',
      accessorKey: 'lineItemName',
      header: 'Name',
      sortable: true,
      sortingFn: nameSort,
      meta: { align: 'left', width: 220 },
      cell: ({ getValue }) => {
        const name = getValue();
        return (
          <Tooltip overlayClassName="mjl-ds" title={name}>
            <span className="campaign-overview__creatives-name">{name}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'flightDates',
      accessorFn: (row) => `${row.startDate} - ${row.endDate}`,
      header: 'Flight Dates',
      sortable: false,
      meta: { align: 'left', width: 200 },
      cell: ({ row }) => (
        <span className="campaign-overview__lineitem-flight">
          {row.original.startDate} &rarr; {row.original.endDate}
        </span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      sortable: false,
      meta: { align: 'center', width: 110 },
      cell: ({ getValue }) => (
        <TagWithConfig {...tagStatusConfig[getValue() || 'PLAY']} />
      ),
    },
    {
      id: 'valueAdd',
      accessorKey: 'valueAdd',
      header: 'Value Add',
      sortable: false,
      meta: { align: 'center', width: 110 },
      cell: ({ getValue }) =>
        getValue() === true ? (
          <Tag
            style={{
              backgroundColor: '#F3E8FF',
              borderRadius: 25,
              color: '#9333EA',
              border: 0,
              fontWeight: 500,
            }}
          >
            Yes
          </Tag>
        ) : null,
    },
    {
      id: 'impressions',
      accessorKey: 'impressions',
      header: 'Impressions',
      sortable: true,
      sortingFn: numericSort('impressions'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => formatNumber(getValue()),
    },
    {
      id: 'clicks',
      accessorKey: 'clicks',
      header: 'Clicks',
      sortable: true,
      sortingFn: numericSort('clicks'),
      meta: { align: 'right', width: 110 },
      cell: ({ getValue }) => formatNumber(getValue()),
    },
    {
      id: 'ctr',
      accessorKey: 'ctr',
      header: 'CTR',
      sortable: true,
      sortingFn: numericSort('ctr'),
      meta: { align: 'right', width: 90 },
      cell: ({ getValue }) => `${formatNumber2SigFig(getValue())}%`,
    },
    {
      id: 'action',
      header: '',
      sortable: false,
      meta: { align: 'right', width: 110 },
      cell: ({ row }) => {
        const r = row.original;
        // data-no-expand: the modal trigger lives inside a card that may be
        // tap-to-expand later; prevent clicks from bubbling up as toggle.
        return (
          <span data-no-expand>
            <LineItemsModal
              byDate={r.byDate}
              summary={{
                startDate: r.startDate,
                endDate: r.endDate,
                impressions: r.impressions,
                clicks: r.clicks,
                ctr: r.ctr,
              }}
              creatives={r.creatives}
              publishers={r.publishers}
              segments={r.segments}
              name={r.lineItemName}
              status={r.status}
            />
          </span>
        );
      },
    },
  ];

  const totalImpressions = coloredData.reduce(
    (sum, d) => sum + (parseFloat(d.impressions) || 0),
    0
  );
  const totalClicks = coloredData.reduce(
    (sum, d) => sum + (parseFloat(d.clicks) || 0),
    0
  );

  const renderMobileCard = (record) => (
    <>
      <div className="campaign-overview__creatives-card-head">
        <div className="campaign-overview__creatives-card-name">
          <span>{record.lineItemName}</span>
        </div>
      </div>
      <div className="campaign-overview__lineitem-card-meta">
        <span className="campaign-overview__lineitem-flight">
          {record.startDate} &rarr; {record.endDate}
        </span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <TagWithConfig {...tagStatusConfig[record.status || 'PLAY']} />
          {record.valueAdd === true && (
            <Tag
              style={{
                backgroundColor: '#F3E8FF',
                borderRadius: 25,
                color: '#9333EA',
                border: 0,
                fontWeight: 500,
              }}
            >
              Value Add
            </Tag>
          )}
        </div>
      </div>
      <div className="campaign-overview__creatives-card-metrics">
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {formatNumber(record.impressions)}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Clicks</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {formatNumber(record.clicks)}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">CTR</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {formatNumber2SigFig(record.ctr)}%
          </span>
        </div>
      </div>
      <div data-no-expand style={{ marginTop: 6 }}>
        <LineItemsModal
          byDate={record.byDate}
          summary={{
            startDate: record.startDate,
            endDate: record.endDate,
            impressions: record.impressions,
            clicks: record.clicks,
            ctr: record.ctr,
          }}
          creatives={record.creatives}
          publishers={record.publishers}
          segments={record.segments}
          name={record.lineItemName}
          status={record.status}
        />
      </div>
    </>
  );

  return (
    <section className="campaign-overview__creatives">
      <header className="campaign-overview__creatives-head">
        <div className="campaign-overview__creatives-eyebrow mono-eyebrow">
          Line Items
        </div>
      </header>

      <div className="campaign-overview__creatives-body">
        <div className="campaign-overview__creatives-table">
          <DataTable
            data={coloredData}
            columns={columns}
            pageSize={10}
            mobileBreakpoint="md"
            renderMobileCard={renderMobileCard}
            summaryRow={{
              labelColumnId: 'lineItemName',
              label: 'Totals + Averages',
              cells: {
                impressions: formatNumber(totalImpressions),
                clicks: formatNumber(totalClicks),
                ctr: '-',
              },
            }}
            emptyState="No line items yet."
          />
        </div>

        <aside className="campaign-overview__creatives-breakdown">
          <div className="campaign-overview__creatives-breakdown-head">
            <div className="campaign-overview__creatives-breakdown-eyebrow mono-eyebrow">
              Breakdown
            </div>
            <Select
              value={lineItemDimension}
              onChange={(v) => setLineItemDimension(v)}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
              className="campaign-overview__creatives-showby"
              size="small"
            >
              {SHOW_BY_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  <MenuItemText text={option.text} />
                </Option>
              ))}
            </Select>
          </div>
          <LineItemsDonut
            data={coloredData}
            height={480}
            selectedDimension={lineItemDimension}
          />
        </aside>
      </div>
    </section>
  );
};

export default LineItemsCard;
